import { useState } from "react";
import api, { errMsg } from "../../lib/api";
import { toast } from "../../lib/notices";
import { DataTable, LoadingBlock, Money, Pill, Section, useAdminResource } from "./AdminUI";
import { Ban, Eye, KeyRound, ShieldCheck, Trash2, UserCog } from "lucide-react";
import { formatDateTime, fullTimestamp, timeAgo } from "../../lib/datetime";

const PLANS = ["free", "creator", "pro", "agency"];
const PAGE = 25;

export default function UsersPanel() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState("");

  const list = useAdminResource("/admin/users", {
    params: { q: query, plan, status, limit: PAGE, skip },
  });
  const detail = useAdminResource(`/admin/users/${openId}`, { enabled: Boolean(openId) });

  const act = async (label, fn) => {
    setBusy(label);
    try {
      const msg = await fn();
      toast.success(msg);
      list.reload();
      if (openId) detail.reload();
    } catch (e) {
      toast.error(errMsg(e, `Could not ${label}`));
    } finally { setBusy(""); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(
      `View the app as ${u.email}?\n\n` +
      `You will be signed in as them until you stop, and everything you do ` +
      `happens to their account. The session is time-limited and recorded in the audit log.`
    )) return;
    setBusy("impersonate");
    try {
      const r = await api.post(`/admin/users/${u.user_id}/impersonate`);
      // Full reload, not a client-side nav: the session cookie has changed and
      // every cached response in memory belongs to the admin, not the target.
      window.location.assign("/dashboard");
      return r;
    } catch (e) {
      toast.error(errMsg(e, "Could not start impersonation"));
      setBusy("");
    }
  };

  const changePlan = (u, next) => act("change the plan", async () => {
    await api.post(`/admin/users/${u.user_id}/plan`, { plan: next, reason: "Changed from the admin console" });
    return `${u.email} moved to ${next}`;
  });

  const suspend = (u) => {
    const reason = window.prompt(`Suspend ${u.email}?\n\nThey are signed out immediately and cannot sign back in.\nReason (shown to them):`, "Suspended by an administrator.");
    if (reason === null) return;
    act("suspend", async () => {
      const r = await api.post(`/admin/users/${u.user_id}/suspend`, { reason });
      return `${u.email} suspended · ${r.data.sessions_revoked} session(s) revoked`;
    });
  };

  const unsuspend = (u) => act("restore", async () => {
    await api.post(`/admin/users/${u.user_id}/unsuspend`);
    return `${u.email} restored`;
  });

  const revoke = (u) => act("revoke sessions", async () => {
    const r = await api.post(`/admin/users/${u.user_id}/revoke-sessions`);
    return `${r.data.revoked} session(s) revoked for ${u.email}`;
  });

  const remove = (u) => {
    const typed = window.prompt(`PERMANENTLY delete ${u.email} and every project they own.\n\nThis cannot be undone. Type the email address to confirm:`);
    if (!typed) return;
    act("delete the account", async () => {
      const r = await api.delete(`/admin/users/${u.user_id}`, {
        data: { confirm_email: typed, reason: "Deleted from the admin console" },
      });
      setOpenId(null);
      return `${u.email} deleted · ${r.data.projects_deleted} project(s) removed`;
    });
  };

  const submitSearch = (e) => { e.preventDefault(); setSkip(0); setQuery(q.trim()); };
  const total = list.data?.total ?? 0;

  return (
    <Section
      id="users"
      title="// USER MANAGEMENT"
      actions={<span className="text-2xs uppercase tracking-widest text-ink-muted tabular-nums">{total} account{total === 1 ? "" : "s"}</span>}
    >
      <form onSubmit={submitSearch} className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[12rem]">
          <label className="sr-only" htmlFor="admin-user-search">Search by email or name</label>
          <input id="admin-user-search" className="brutal-input w-full" placeholder="Search email or name…"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
        </div>
        <div>
          <label className="sr-only" htmlFor="admin-user-plan">Filter by plan</label>
          <select id="admin-user-plan" className="brutal-select" value={plan}
                  onChange={(e) => { setSkip(0); setPlan(e.target.value); }} data-testid="admin-user-plan">
            <option value="">All plans</option>
            {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="sr-only" htmlFor="admin-user-status">Filter by status</label>
          <select id="admin-user-status" className="brutal-select" value={status}
                  onChange={(e) => { setSkip(0); setStatus(e.target.value); }} data-testid="admin-user-status">
            <option value="">Any status</option>
            <option value="suspended">Suspended</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>
        <button type="submit" className="brutal-btn">Search</button>
      </form>

      {list.loading && <LoadingBlock label="Loading users" />}
      {list.error && <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{list.error}</div>}

      {!list.loading && !list.error && (
        <>
          <DataTable
            testid="admin-users-table"
            caption="User accounts, newest first"
            empty="No accounts match these filters."
            columns={[
              { key: "email", label: "Email" }, { key: "plan", label: "Plan" },
              { key: "projects", label: "Projects" }, { key: "renders", label: "Renders (mo)" },
              { key: "state", label: "State" }, { key: "last_login", label: "Last login" },
              { key: "joined", label: "Joined" },
              { key: "actions", label: "Actions", srOnly: true },
            ]}
            rows={list.data?.users}
            renderRow={(u) => (
              <tr key={u.user_id} className="border-t border-rule align-middle">
                <td className="p-3 font-mono-ui text-xs break-all">
                  <button type="button" className="underline hover:text-ink-accent text-left"
                          onClick={() => setOpenId(openId === u.user_id ? null : u.user_id)}
                          aria-expanded={openId === u.user_id}
                          data-testid={`admin-user-${u.user_id}`}>{u.email}</button>
                  {u.is_admin && <span className="ml-2"><Pill tone="accent">ADMIN</Pill></span>}
                </td>
                <td className="p-3">
                  <label className="sr-only" htmlFor={`plan-${u.user_id}`}>Plan for {u.email}</label>
                  <select id={`plan-${u.user_id}`} className="brutal-select text-xs" value={u.plan || "free"}
                          disabled={busy !== ""} onChange={(e) => changePlan(u, e.target.value)}>
                    {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td className="p-3 tabular-nums">{u.project_count}</td>
                <td className="p-3 tabular-nums">{u.renders_this_month}</td>
                <td className="p-3">
                  {u.suspended ? <Pill tone="danger">SUSPENDED</Pill>
                    : !u.email_verified ? <Pill tone="warning">UNVERIFIED</Pill>
                    : <Pill tone="success">ACTIVE</Pill>}
                </td>
                <td className="p-3 font-mono-ui text-xs whitespace-nowrap" title={fullTimestamp(u.last_login_at)}>
                  {u.last_login_at ? (
                    <>
                      {formatDateTime(u.last_login_at)}
                      <div className="text-xs text-ink-subtle">{timeAgo(u.last_login_at)}</div>
                    </>
                  ) : (
                    <span className="text-ink-subtle">never</span>
                  )}
                </td>
                <td className="p-3 font-mono-ui text-xs text-ink-muted whitespace-nowrap" title={fullTimestamp(u.created_at)}>{formatDateTime(u.created_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                            onClick={() => impersonate(u)} disabled={busy !== "" || u.is_admin || u.suspended}
                            aria-label={`View the app as ${u.email}`}
                            title={u.is_admin ? "Admins cannot be impersonated"
                                   : u.suspended ? "Suspended accounts cannot be impersonated"
                                   : "View as this user"}>
                      <Eye size={12} aria-hidden="true" />
                    </button>
                    <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                            onClick={() => revoke(u)} disabled={busy !== ""}
                            aria-label={`Revoke all sessions for ${u.email}`} title="Revoke sessions">
                      <KeyRound size={12} aria-hidden="true" />
                    </button>
                    {u.suspended ? (
                      <button type="button" className="brutal-btn brutal-btn-sm"
                              onClick={() => unsuspend(u)} disabled={busy !== ""}
                              aria-label={`Restore ${u.email}`} title="Restore account">
                        <ShieldCheck size={12} aria-hidden="true" />
                      </button>
                    ) : (
                      <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                              onClick={() => suspend(u)} disabled={busy !== "" || u.is_admin}
                              aria-label={`Suspend ${u.email}`} title={u.is_admin ? "Admins cannot be suspended" : "Suspend account"}>
                        <Ban size={12} aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                            onClick={() => remove(u)} disabled={busy !== "" || u.is_admin}
                            aria-label={`Delete ${u.email}`} title={u.is_admin ? "Admins cannot be deleted" : "Delete account"}>
                      <Trash2 size={12} aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
          />

          <div className="flex items-center justify-between gap-3 mt-3">
            <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                    disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>← Previous</button>
            <span className="text-2xs uppercase tracking-widest text-ink-muted tabular-nums">
              {total === 0 ? "0" : `${skip + 1}–${Math.min(skip + PAGE, total)} of ${total}`}
            </span>
            <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                    disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Next →</button>
          </div>
        </>
      )}

      {openId && (
        <div className="brutal-card p-6 mt-6" data-testid="admin-user-detail">
          <h3 className="marquee-tag mb-4"><UserCog size={12} className="inline mr-1" aria-hidden="true" />// ACCOUNT DETAIL</h3>
          {detail.loading && <LoadingBlock label="Loading account" />}
          {detail.error && <p className="text-ink-accent text-sm" role="alert">{detail.error}</p>}
          {detail.data && (
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <dl className="space-y-1">
                  {[["Email", detail.data.user.email], ["Name", detail.data.user.name],
                    ["User ID", detail.data.user.user_id], ["Plan", detail.data.user.plan],
                    ["Verified", String(detail.data.user.email_verified)],
                    ["Auth", detail.data.user.auth_provider],
                    ["Last login", detail.data.user.last_login_at
                        ? `${formatDateTime(detail.data.user.last_login_at)} (${timeAgo(detail.data.user.last_login_at)})` : "never"],
                    ["Last login IP", detail.data.user.last_login_ip || "—"],
                    ["Active sessions", detail.data.active_sessions],
                    ["Teams", detail.data.teams.length]].map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <dt className="text-2xs uppercase tracking-widest text-ink-muted w-32 shrink-0 pt-1">{k}</dt>
                      <dd className="font-mono-ui text-xs break-all">{String(v ?? "—")}</dd>
                    </div>
                  ))}
                </dl>
                {detail.data.user.suspended && (
                  <p className="mt-3 text-xs text-ink-accent">
                    Suspended {formatDateTime(detail.data.user.suspended_at)} — {detail.data.user.suspended_reason}
                  </p>
                )}
              </div>
              <div>
                <h4 className="text-2xs uppercase tracking-widest text-ink-muted mb-2">Transactions</h4>
                {detail.data.transactions.length === 0
                  ? <p className="text-ink-subtle text-xs mb-4">None.</p>
                  : (
                    <ul className="space-y-1 mb-4">
                      {detail.data.transactions.slice(0, 5).map((t) => (
                        <li key={t.txn_id} className="font-mono-ui text-xs flex justify-between gap-2">
                          <span>{formatDateTime(t.created_at)} · {t.plan}</span>
                          <span><Money amount={t.amount} /> {t.payment_status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                <h4 className="text-2xs uppercase tracking-widest text-ink-muted mb-2">Recent projects</h4>
                {detail.data.projects.length === 0
                  ? <p className="text-ink-subtle text-xs">None.</p>
                  : (
                    <ul className="space-y-1">
                      {detail.data.projects.slice(0, 6).map((p) => (
                        <li key={p.project_id} className="text-xs flex justify-between gap-2">
                          <span className="truncate">{p.title}</span>
                          <span className="text-ink-muted whitespace-nowrap">
                            <span className={`status-dot status-${p.status}`} /> {p.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
